import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  countriesAPI,
  zonesAPI,
  type Country,
  type Zone,
  type ZoneCreatePayload,
} from "../../../services/api";

function getCountryName(zone: Zone, countries: Country[]) {
  if (zone.country_name) {
    return zone.country_name;
  }
  const id = zone.country;
  if (id === null) {
    return "—";
  }
  const c = countries.find(p => String(p.id) === String(id));
  return c?.name ?? String(id);
}

export default function ZonePage() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ZoneCreatePayload>({
    name: "",
    country: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<Zone | null>(null);
  const [deleting, setDeleting] = useState<Zone | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCountries = async () => {
    try {
      const list = await countriesAPI.list();
      setCountries(Array.isArray(list) ? list : []);
    } catch {
      setCountries([]);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const list = await zonesAPI.list();
      setZones(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Failed to load zones");
      setZones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);
  useEffect(() => {
    fetchList();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Zone name is required");
      return;
    }
    if (!form.country) {
      toast.error("Please select a country");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        country: Number(form.country) || form.country,
        is_active: form.is_active,
      };
      if (editing) {
        await zonesAPI.update(editing.id, payload);
        toast.success("Zone updated successfully");
      } else {
        await zonesAPI.create(payload);
        toast.success("Zone created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", country: "", is_active: true });
      setEditing(null);
      fetchList();
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save zone";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await zonesAPI.delete(deleting.id);
      toast.success("Zone deleted successfully");
      setDeleting(null);
      fetchList();
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete zone";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Zone</h1>
        <p className="text-muted-foreground">Manage zones</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Zones ({zones.length})</CardTitle>
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", country: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    country:
                      countries[0]?.id !== null ? String(countries[0].id) : "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit Zone" : "Create New Zone"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Country *</Label>
                  <Select
                    value={form.country}
                    onValueChange={v =>
                      setForm(prev => ({ ...prev, country: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zone-name">Name *</Label>
                  <Input
                    id="zone-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Zone name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="zone-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="zone-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : zones.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              No zones yet. Create one to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zones.map((z, i) => (
                  <TableRow key={z.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium">{z.name}</TableCell>
                    <TableCell>{getCountryName(z, countries)}</TableCell>
                    <TableCell>
                      {z.is_active !== false ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(z);
                            setForm({
                              name: z.name,
                              country:
                                z.country !== null
                                  ? String(z.country)
                                  : countries[0]?.id !== null
                                    ? String(countries[0].id)
                                    : "",
                              is_active: z.is_active ?? true,
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleting(z)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Zone</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
